import React, { memo, useCallback, useMemo, useState } from 'react'
import {
    Box,
    IconButton,
    TableCell,
    TableRow,
    Typography,
    alpha,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button
} from '@mui/material'
import { useSelector, useDispatch } from 'react-redux'
import { Cancel, Close } from '@mui/icons-material'

import { OrderStatus, productToJSON, Broker_OrderType, Order, OrderType } from '../../proto/generated/order'
import {
    OrderBatchGroup,
    effectiveOrderFilledQuantity,
    getCancellableBasketOrderIds,
    getIcebergBasketOrderIds,
    isBasketIcebergBatchStrategyAux,
    isIcebergGroup,
    pickRepresentativeNonTerminalIcebergLeg,
} from '../../util/icebergOrders'
import { selectPositions, selectGroupBy } from '../../selectors/positions'
import { getGroupedPositions } from '../../util/positions'
import { GroupedPosition } from '../../reducer/positions.reducer'
import { OrdersNavButtonsDetailed } from '../postionsWidget'
import OrderRow from './OrderRow'
import OrderStatusDisplay from '../orderStatus/orderStatusDisplay'
import ThinArrowDownIcon from '../../assets/icons/ThinArrowDownIcon'
import ThinArrowRightIcon from '../../assets/icons/ThinArrowRightIcon'
import { selectOrdersHistory } from '../../selectors/orders'
import { selectPrimaryTradingAccount } from '../../selectors/user'
import { cancelOrder } from '../../services/apis/orders'
import { updateOrderHistory } from '../../reducer/orders.reducer'
import commonActions from '../../actions/common.actions'
import MoreHorizontalIcon from '../../assets/icons/MoreHorizontalIcon'
import { ExtraSmallButton } from '../custom'
import {
    calculateGcd,
    isPlaceholderStrategyGroupName,
    resolveBasketBatchStrategyGroupName,
    classifyBasketOrderSection,
    basketOrderSectionTitle,
    BASKET_ORDER_SECTION_ORDER,
    type BasketOrderSectionKey,
} from '../../util/orders'

const orderTypeToString = (type: Broker_OrderType): string => {
    switch (type) {
        case Broker_OrderType.MARKET_ORDER: return "MARKET";
        case Broker_OrderType.LIMIT_ORDER: return "LIMIT";
        case Broker_OrderType.STOPLOSS_ORDER: return "SL";
        case Broker_OrderType.STOPLOSS_MARKET_ORDER: return "SL-M";
        default: return "UNKNOWN";
    }
};

export interface OrderBatchGroupRowProps {
    batchGroup: OrderBatchGroup;
    tabType: OrdersNavButtonsDetailed;
    hideMetaData?: boolean;
}

const OrderBatchGroupRow: React.FC<OrderBatchGroupRowProps> = ({ batchGroup, tabType, hideMetaData = false }) => {
    const dispatch = useDispatch();
    const [expanded, setExpanded] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const positions = useSelector(selectPositions);
    const groupBy = useSelector(selectGroupBy);
    const orderHistory = useSelector(selectOrdersHistory);
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount);

    const menuOpen = Boolean(anchorEl);

    const hasIndexedIcebergInBatch = useMemo(
        () =>
            orderHistory.some(
                (o) =>
                    o.basketId === batchGroup.basketId &&
                    o.orderType === OrderType.ICEBERG &&
                    Boolean(o.orderExecutionParams?.icebergNumberOfLegs) &&
                    o.orderExecutionParams?.icebergLegNumber != null
            ),
        [orderHistory, batchGroup.basketId]
    );
    const strategyBasketOnly = !hasIndexedIcebergInBatch;
    const hasIcebergLadderLegs = hasIndexedIcebergInBatch;

    const groupedPositions: GroupedPosition[] = useMemo(() => {
        const grouped: Record<string, GroupedPosition> = getGroupedPositions(positions, groupBy)
        return Object.values(grouped)
    }, [positions, groupBy])

    const position = useMemo(() => {
        return groupedPositions.find(p => p.groupId === batchGroup.basketId)
    }, [groupedPositions, batchGroup.basketId])

    const batchOrdersLive = useMemo(
        () =>
            !hasIcebergLadderLegs
                ? orderHistory.filter((o) => o.basketId === batchGroup.basketId)
                : orderHistory.filter(
                      (o) =>
                          o.basketId === batchGroup.basketId &&
                          o.orderType === OrderType.ICEBERG &&
                          o.orderExecutionParams?.icebergNumberOfLegs &&
                          o.orderExecutionParams?.icebergLegNumber != null
                  ),
        [orderHistory, batchGroup.basketId, hasIcebergLadderLegs]
    );

    const batchOrdersAllStatuses = useMemo(
        () => orderHistory.filter((o) => o.basketId === batchGroup.basketId),
        [orderHistory, batchGroup.basketId]
    );

    /**
     * Same semantics as nested {@link OrderRow}: `executedLegs` / `icebergNumberOfLegs` from full history
     * per security; parent shows the minimum across securities so the basket row matches each leg row.
     */
    const basketIcebergLegSubtitle = useMemo(() => {
        if (!hasIcebergLadderLegs || !batchGroup.batchId || !batchGroup.basketId) return null;
        const pool = batchOrdersLive.length ? batchOrdersLive : batchGroup.allOrders;
        const sampleIce = pool.find((o) => o.orderExecutionParams?.icebergNumberOfLegs != null);
        const nIce = sampleIce?.orderExecutionParams?.icebergNumberOfLegs;
        if (nIce == null) return null;

        const secKeys = [
            ...new Set(
                pool
                    .filter(
                        (o) =>
                            o.orderType === OrderType.ICEBERG &&
                            Boolean(o.orderExecutionParams?.icebergNumberOfLegs) &&
                            o.orderExecutionParams?.icebergLegNumber != null &&
                            Boolean(o.security?.code)
                    )
                    .map((o) => `${o.security?.exchange ?? 0}_${o.security?.code ?? ''}`)
            ),
        ];

        if (!secKeys.length) {
            const executed = orderHistory.filter(
                (o) =>
                    o.basketId === batchGroup.basketId &&
                    o.orderType === OrderType.ICEBERG &&
                    Boolean(o.orderExecutionParams?.icebergNumberOfLegs) &&
                    o.orderExecutionParams?.icebergLegNumber != null &&
                    o.status === OrderStatus.EXECUTED
            ).length;
            return { executed, total: Math.max(1, nIce) };
        }

        const perSecurityExecuted = secKeys.map((k) =>
            orderHistory.filter(
                (o) =>
                    o.basketId === batchGroup.basketId &&
                    o.orderType === OrderType.ICEBERG &&
                    Boolean(o.orderExecutionParams?.icebergNumberOfLegs) &&
                    o.orderExecutionParams?.icebergLegNumber != null &&
                    `${o.security?.exchange ?? 0}_${o.security?.code ?? ''}` === k &&
                    o.status === OrderStatus.EXECUTED
            ).length
        );
        return { executed: Math.min(...perSecurityExecuted), total: Math.max(1, nIce) };
    }, [
        hasIcebergLadderLegs,
        batchGroup.basketId,
        batchGroup.allOrders,
        batchOrdersLive,
        orderHistory,
    ]);

    const representativeOrder = useMemo(() => {
        const pool = batchOrdersLive.length ? batchOrdersLive : batchGroup.allOrders;
        if (hasIcebergLadderLegs) {
            const active = pickRepresentativeNonTerminalIcebergLeg(pool);
            if (active) return active;
        }
        return (
            pool.find(
                (o) =>
                    o.status !== OrderStatus.CANCELLED &&
                    o.status !== OrderStatus.FAILED &&
                    o.status !== OrderStatus.REJECTED
            ) || pool[0]
        );
    }, [batchOrdersLive, batchGroup.allOrders, hasIcebergLadderLegs]);

    const expandRows = useMemo(() => {
        const basePool = batchOrdersAllStatuses.length ? batchOrdersAllStatuses : batchGroup.allOrders;
        const seen = new Set<string>();
        const pool = basePool.filter((o) => {
            const key =
                o.orderId ||
                o.clientOrderId ||
                `${o.batchId ?? ''}|${o.basketId ?? ''}|${o.security?.exchange ?? 0}|${o.security?.code ?? ''}|${o.created ?? 0}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
        if (strategyBasketOnly || !hasIcebergLadderLegs) {
            return [...pool].sort((a, b) => (a.created ?? 0) - (b.created ?? 0));
        }

        // Android parity: show one aggregated primary row per security, not raw lifecycle rows.
        const primaryLegs = pool.filter(
            (o) =>
                o.orderType === OrderType.ICEBERG &&
                Boolean(o.orderExecutionParams?.icebergNumberOfLegs) &&
                o.orderExecutionParams?.icebergLegNumber != null
        );
        const bySecurity = new Map<string, Order[]>();
        primaryLegs.forEach((o) => {
            const key = `${o.security?.exchange ?? 0}_${o.security?.code ?? ''}`;
            const arr = bySecurity.get(key);
            if (arr) arr.push(o);
            else bySecurity.set(key, [o]);
        });
        const aggregatedGroups: OrderBatchGroup[] = [...bySecurity.entries()]
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([, secOrders]) => {
                const representative =
                    pickRepresentativeNonTerminalIcebergLeg(secOrders) ||
                    secOrders.find(
                        (o) =>
                            o.status !== OrderStatus.CANCELLED &&
                            o.status !== OrderStatus.FAILED &&
                            o.status !== OrderStatus.REJECTED
                    ) ||
                    secOrders[0];

                const sorted = [...secOrders].sort(
                    (a, b) =>
                        (a.orderExecutionParams?.icebergLegNumber ?? 0) -
                            (b.orderExecutionParams?.icebergLegNumber ?? 0) ||
                        (a.created ?? 0) - (b.created ?? 0)
                );
                const nLegs = sorted[0]?.orderExecutionParams?.icebergNumberOfLegs ?? 1;
                const perSliceQty = sorted[0]?.quantity ?? 0;
                const totalQty = Math.max(1, nLegs) * perSliceQty;
                const filledQty = sorted.reduce((sum, o) => sum + effectiveOrderFilledQuantity(o), 0);
                const executedLegs = sorted.filter((o) => o.status === OrderStatus.EXECUTED).length;
                return {
                    isIcebergGroup: true,
                    allOrders: sorted,
                    totalQuantity: totalQty,
                    totalFilledQuantity: filledQty,
                    totalLegs: Math.max(1, nLegs),
                    executedLegs,
                    batchId: representative.batchId || batchGroup.batchId,
                    basketId: representative.basketId || batchGroup.basketId,
                    representativeOrder: representative,
                    isBasketGroup: false,
                    securitiesCount: 1,
                };
            });

        const aux = pool
            .filter((o) => isBasketIcebergBatchStrategyAux(o))
            .sort((a, b) => (a.created ?? 0) - (b.created ?? 0));
        const consumedIds = new Set<string>();
        primaryLegs.forEach((o) => {
            if (o.orderId) consumedIds.add(o.orderId);
            if (o.clientOrderId) consumedIds.add(o.clientOrderId);
        });
        aux.forEach((o) => {
            if (o.orderId) consumedIds.add(o.orderId);
            if (o.clientOrderId) consumedIds.add(o.clientOrderId);
        });
        const leftovers = pool
            .filter((o) => {
                const id = o.orderId || o.clientOrderId;
                if (id && consumedIds.has(id)) return false;
                if (
                    o.orderType === OrderType.ICEBERG &&
                    Boolean(o.orderExecutionParams?.icebergNumberOfLegs) &&
                    o.orderExecutionParams?.icebergLegNumber != null
                ) {
                    return false;
                }
                if (isBasketIcebergBatchStrategyAux(o)) return false;
                return true;
            })
            .sort((a, b) => (a.created ?? 0) - (b.created ?? 0));
        return [...aggregatedGroups, ...aux, ...leftovers];
    }, [strategyBasketOnly, hasIcebergLadderLegs, batchOrdersAllStatuses, batchGroup.allOrders]);

    const sectionKeyForItem = useCallback((item: Order | OrderBatchGroup): BasketOrderSectionKey => {
        if (isIcebergGroup(item)) {
            return classifyBasketOrderSection(item.representativeOrder || item.allOrders[0]);
        }
        return classifyBasketOrderSection(item);
    }, []);

    const groupedExpandSections = useMemo(() => {
        const buckets: Record<BasketOrderSectionKey, (Order | OrderBatchGroup)[]> = {
            entry: [],
            conditional: [],
            slTarget: [],
            adjust: [],
            exit: [],
        };
        expandRows.forEach((item) => {
            buckets[sectionKeyForItem(item)].push(item);
        });
        return BASKET_ORDER_SECTION_ORDER.filter((key) => buckets[key].length > 0).map((key) => ({
            key,
            title: basketOrderSectionTitle(key),
            rows: buckets[key],
        }));
    }, [expandRows, sectionKeyForItem]);

    const handleToggleExpand = useCallback(() => {
        setExpanded(!expanded);
    }, [expanded]);

    const handleMenuOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    }, []);

    const handleMenuClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const handleCancel = useCallback(async () => {
        if (!batchGroup.basketId || !batchGroup.batchId) return;

        const orderIds = strategyBasketOnly
            ? getCancellableBasketOrderIds(orderHistory, batchGroup.basketId)
            : getIcebergBasketOrderIds(orderHistory, batchGroup.basketId);

        if (orderIds.length === 0) {
            commonActions.setApiError(dispatch, {
                error: true,
                message: 'No open orders found',
                errorType: 'info'
            });
            return;
        }

        const idSet = new Set(orderIds);
        const cancellableOrders = orderHistory.filter(
            (o) => o.basketId === batchGroup.basketId && o.orderId && idSet.has(o.orderId)
        );
        const cancellableById = new Map(
            cancellableOrders
                .filter((o): o is Order & { orderId: string } => Boolean(o.orderId))
                .map((o) => [o.orderId, o])
        );

        try {
            const res = await cancelOrder({
                dispatch,
                body: {
                    tradingAccountId: primaryTradingAccount?.accountId ?? "",
                    orderId: orderIds,
                }
            });

            const updates: Order[] = [];
            res.orderResponse.forEach((responseOrder) => {
                if (!responseOrder) return;
                const { successful, commitOrder, errorMessage, order: respOrder } = responseOrder;

                if (!successful) {
                    commonActions.setApiError(dispatch, {
                        error: true,
                        message: errorMessage || 'Unable to cancel order',
                        errorType: 'error'
                    })
                } else if (commitOrder) {
                    const id = respOrder?.orderId || responseOrder.orderId;
                    const orderToUpdate = id ? cancellableById.get(id) : undefined;
                    if (orderToUpdate) {
                        updates.push({ ...orderToUpdate, status: OrderStatus.CANCELLED });
                    }
                }
            });

            if (updates.length > 0) {
                dispatch(updateOrderHistory({ updates }));
            }
        } catch (error) {
            commonActions.setApiError(dispatch, {
                error: true,
                message: 'Error cancelling orders',
                errorType: 'error'
            });
        }
    }, [dispatch, batchGroup.basketId, orderHistory, primaryTradingAccount, strategyBasketOnly]);

    const handleCancelClick = useCallback(() => {
        handleMenuClose();
        setCancelDialogOpen(true);
    }, [handleMenuClose]);

    const handleCancelConfirm = useCallback(() => {
        setCancelDialogOpen(false);
        handleCancel();
    }, [handleCancel]);

    const displayName = useMemo(() => {
        const fromPos = position?.groupName?.trim()
        if (fromPos && !isPlaceholderStrategyGroupName(fromPos)) return fromPos
        const resolved = resolveBasketBatchStrategyGroupName(orderHistory, batchGroup.basketId)
        if (resolved) return resolved
        const rep = representativeOrder?.strategyGroup?.trim()
        if (rep && !isPlaceholderStrategyGroupName(rep)) return rep
        return `Basket ${batchGroup.basketId}`
    }, [position, representativeOrder, batchGroup.basketId, orderHistory]);

    const parentLegProgress = useMemo(() => {
        if (!hasIcebergLadderLegs) {
            return { filled: batchGroup.executedLegs, total: Math.max(1, batchGroup.totalLegs) };
        }

        const firstOrder = batchGroup.allOrders[0];
        const nIce = firstOrder?.orderExecutionParams?.icebergNumberOfLegs;
        const pool = batchOrdersLive.length ? batchOrdersLive : batchGroup.allOrders;
        const uniqueSecurities =
            batchGroup.securitiesCount ||
            new Set(
                batchGroup.allOrders.map((o) => `${o.security?.exchange ?? 0}_${o.security?.code ?? ''}`)
            ).size;
        if (!nIce) {
            const current = Math.floor(batchGroup.executedLegs / uniqueSecurities);
            const total = Math.floor(batchGroup.totalLegs / uniqueSecurities);
            return { filled: current, total };
        }
        const defaultLegQty = batchGroup.allOrders.find((o) => (o.quantity ?? 0) > 0)?.quantity ?? 0;
        const secKeys = [
            ...new Set(
                batchGroup.allOrders
                    .filter((o) => o.security?.code)
                    .map((o) => `${o.security?.exchange ?? 0}_${o.security?.code}`)
            ),
        ];
        const filledList: number[] = [];
        const totalList: number[] = [];
        for (const k of secKeys) {
            const legs = pool.filter(
                (o) => `${o.security?.exchange ?? 0}_${o.security?.code ?? ''}` === k
            );
            const n = legs[0]?.orderExecutionParams?.icebergNumberOfLegs ?? nIce;
            const legQty = legs[0]?.quantity ?? defaultLegQty;
            const qtyUnit = legQty > 0 ? legQty : defaultLegQty;
            const totalQty = Math.round(n * (qtyUnit > 0 ? qtyUnit : 1));
            const filledQty = Math.round(
                legs.reduce((sum, o) => sum + effectiveOrderFilledQuantity(o), 0)
            );
            filledList.push(filledQty);
            totalList.push(totalQty);
        }
        if (!filledList.length) {
            return { filled: Math.floor(batchGroup.executedLegs / uniqueSecurities), total: nIce };
        }
        const gFilled = calculateGcd(filledList);
        const gTotal = calculateGcd(totalList);
        return {
            filled: gFilled,
            total: gTotal > 0 ? gTotal : Math.round(nIce * (defaultLegQty > 0 ? defaultLegQty : 1)),
        };
    }, [hasIcebergLadderLegs, batchGroup.allOrders, batchGroup.executedLegs, batchGroup.totalLegs, batchGroup.securitiesCount, batchOrdersLive]);

    const timestamp = representativeOrder?.exchangeTimestamp || representativeOrder?.created || 0;
    const date = new Date(timestamp);
    const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

    const symbolRowPadding = useMemo(() => {
        return hideMetaData ? '5px' : '5px 5px 0'
    }, [hideMetaData]);
    const symbolRowBorderBottom = useMemo(() => {
        return hideMetaData ? (theme: any) => `1.5px solid ${theme.palette.background.default}` : 'none'
    }, [hideMetaData]);

    return (
        <>
            <TableRow
                className="basket-row-first"
                sx={{
                    opacity: [OrderStatus.CANCELLED, OrderStatus.FAILED].includes(representativeOrder?.status ?? 0) ? 0.5 : 1,
                    position: 'relative',
                    cursor: 'pointer',
                    '&:hover .edit-icon-order': { display: 'table-cell !important' },
                    '&:hover .edit-icon-replacement': { display: 'none !important' },
                    '&:hover .order-actions': { visibility: 'visible' },
                }}
                onClick={handleToggleExpand}
            >
                <TableCell sx={{ width: '36%', padding: symbolRowPadding, paddingLeft: 0, verticalAlign: 'top', borderBottom: symbolRowBorderBottom }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, px: 1 }}>
                        <Typography
                            fontSize={12}
                            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            title={displayName}
                        >
                            {displayName}
                        </Typography>
                        <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                            {expanded ? <ThinArrowDownIcon sx={{ fontSize: '18px' }} /> : <ThinArrowRightIcon sx={{ fontSize: '18px' }} />}
                        </span>
                    </Box>
                </TableCell>

                <TableCell sx={{ width: '5%', padding: symbolRowPadding, textAlign: 'center', verticalAlign: 'top', borderBottom: symbolRowBorderBottom }}>
                    <Typography fontSize="13px" color="text.secondary">
                        -
                    </Typography>
                </TableCell>

                <TableCell sx={{ width: '18%', padding: symbolRowPadding, textAlign: 'right', verticalAlign: 'top', borderBottom: symbolRowBorderBottom }}>
                    <Typography fontSize="13px">
                        {`${parentLegProgress.filled}/${parentLegProgress.total}`}
                    </Typography>
                </TableCell>

                <TableCell sx={{ width: '23%', padding: symbolRowPadding, textAlign: 'right', verticalAlign: 'top', borderBottom: (theme) => `1.5px solid ${theme.palette.background.default}` }} rowSpan={hideMetaData ? 1 : 2}>
                    <Typography fontSize="13px" color="text.secondary">
                        -
                    </Typography>
                </TableCell>

                <TableCell sx={{ width: '18%', padding: symbolRowPadding, pr: '2px', textAlign: 'right', verticalAlign: 'top', borderBottom: (theme) => `1.5px solid ${theme.palette.background.default}`, position: 'relative' }} rowSpan={hideMetaData ? 1 : 2}>
                    <Typography fontSize="13px" color="text.secondary">
                        -
                    </Typography>
                    <Box className="order-actions"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            height: '100%',
                            display: 'flex',
                            visibility: 'hidden',
                            background: (theme) => alpha(theme.palette.background.paper, 0.9),
                            alignItems: 'center',
                            zIndex: 2,
                            borderRadius: '4px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <ExtraSmallButton 
                            size="small" 
                            onClick={handleMenuOpen} 
                            sx={{ p: 0, height: '100%', width: '36px', borderRadius: '0 !important' }}
                        >
                            <MoreHorizontalIcon fontSize="small" />
                        </ExtraSmallButton>
                    </Box>
                </TableCell>
            </TableRow>

            {!hideMetaData && (
                <TableRow
                    className="basket-row-second"
                    sx={{
                        borderBottom: symbolRowBorderBottom,
                        opacity: [OrderStatus.CANCELLED, OrderStatus.FAILED].includes(representativeOrder?.status ?? 0) ? 0.5 : 1,
                        position: 'relative',
                        cursor: 'pointer',
                        '&:hover .edit-icon-order': { display: 'table-cell !important' },
                        '&:hover .edit-icon-replacement': { display: 'none !important' },
                        '&:hover .order-actions': { visibility: 'visible' },
                    }}
                    onClick={handleToggleExpand}
                >
                    <TableCell sx={{ padding: '0 0 5px', verticalAlign: 'top', borderBottom: (theme) => `1.5px solid ${theme.palette.background.default}` }} colSpan={3}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', px: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.3 }}>
                                <Typography fontSize="12px" color="text.secondary">{time}</Typography>
                                <Typography fontSize="12px" marginLeft={'6px'} color="text.secondary" variant="caption">
                                    {productToJSON(representativeOrder?.product)}
                                </Typography>
                                {basketIcebergLegSubtitle && (
                                    <Typography fontSize="11px" color="text.secondary" marginLeft={'6px'}>
                                        • {basketIcebergLegSubtitle.executed}/{basketIcebergLegSubtitle.total} legs
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </TableCell>
                </TableRow>
            )}

            {expanded &&
                groupedExpandSections.map((section) => (
                    <React.Fragment key={`basket-section-${section.key}`}>
                        <TableRow>
                            <TableCell
                                colSpan={5}
                                sx={{
                                    py: 0.75,
                                    pt: 1.25,
                                    px: 1,
                                    borderBottom: 'none',
                                }}
                            >
                                <Typography
                                    fontSize={10}
                                    fontWeight={600}
                                    color="text.secondary"
                                    letterSpacing={0.6}
                                    sx={{ textTransform: 'uppercase' }}
                                >
                                    {section.title}
                                </Typography>
                            </TableCell>
                        </TableRow>
                        {section.rows.map((orderOrGroup, index) => {
                            const oid = isIcebergGroup(orderOrGroup)
                                ? `${orderOrGroup.batchId}-${index}`
                                : (orderOrGroup as Order).orderId || (orderOrGroup as Order).clientOrderId || String(index)
                            return (
                                <OrderRow
                                    key={`batch-leg-${section.key}-${oid}`}
                                    order={orderOrGroup}
                                    tabType={tabType}
                                    hideOrderStatus={hideMetaData}
                                    isNestedInBasket={true}
                                />
                            )
                        })}
                    </React.Fragment>
                ))}

            <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                disableAutoFocusItem
                disableEnforceFocus
                PaperProps={{
                    sx: {
                        ml: 1,
                        minWidth: '100px',
                        '& .MuiMenu-list': { paddingTop: 0, paddingBottom: 0 }
                    }
                }}
            >
                <MenuItem onClick={handleCancelClick} sx={{ px: '5px' }}>
                    <ListItemIcon sx={{ minWidth: '30px !important' }}>
                        <Cancel sx={{ fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText>
                        <Typography fontSize={'12px'}>Cancel</Typography>
                    </ListItemText>
                </MenuItem>
            </Menu>

            <Dialog 
                open={cancelDialogOpen} 
                onClose={() => setCancelDialogOpen(false)} 
                PaperProps={{ sx: { borderRadius: 0.4, minWidth: 300 } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Cancel basket orders
                    <IconButton onClick={() => setCancelDialogOpen(false)} size="small" sx={{ ml: 1 }}>
                        <Close sx={{ fontSize: 18 }} />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to cancel all orders in this basket? This will cancel all legs.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button 
                        onClick={() => setCancelDialogOpen(false)} 
                        variant="outlined" 
                        size="small" 
                        sx={{ textTransform: 'none', borderRadius: '4px' }}
                    >
                        NO
                    </Button>
                    <Button 
                        onClick={handleCancelConfirm} 
                        variant="contained" 
                        size="small" 
                        sx={{ textTransform: 'none', borderRadius: '4px' }}
                    >
                        YES
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default memo(OrderBatchGroupRow);
